/**
 * THE INBOX'S "MOONX TEAM" TAB (design 24, T17 part A) - how to reach the people behind MoonX.
 *
 * A CHAT WITH THE TEAM IS NOT BUILT. It was ruled out of v1 - "both deferred - e-mail is enough for
 * v1" (D37, 2026-08-21, tickets and chat) - and the owner has had it planned in a session of its
 * own, which starts by reopening D37 (design 24, T17 part B). When it lands, it lands in this tab,
 * with no new sidebar entry. Until then the tab says so and gives the team's two e-mail addresses.
 *
 * THE ADDRESSES ARE THE SUPPORT PAGE'S OWN: `getPlatformContacts`, read through the support
 * feature's one seam (`usePlatformContacts`) and named and glossed in its words (`support` `page.*`), so
 * the two pages can never give different addresses or describe them differently. ABSENT IS SHOWN AS
 * ABSENT, the contract's rule the Support page keeps (features/support/components/ContactBlock.tsx):
 * an address not yet published is never replaced by a placeholder. Not that page's block itself:
 * its heading ("Still stuck?") and its pointers to "the guides above" belong to the Support page.
 */
import { Anchor, Button, Group, Stack, Text } from '@mantine/core';
import { Mail } from '@moonx/ui';
import { CabinetPageHeader, SectionCard } from '../../features/cabinet/components';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { CABINET_ROUTES as SUPPORT_PLACES, usePlatformContacts } from '../../features/support';
import { Trans, useTranslation } from '../../i18n';

export function TeamTab() {
  const { t } = useTranslation('engagement');
  return (
    <Stack gap="lg">
      <CabinetPageHeader title={t('inbox.team.title')} lede={t('inbox.team.lede')} />
      <SectionCard title={t('inbox.team.writeToUs')} icon={Mail}>
        <TeamAddresses />
      </SectionCard>
      <Text size="sm" c="dimmed">
        <Trans
          t={t}
          i18nKey="inbox.team.guides"
          components={{
            // Trans puts the sentence's own link words inside it. Not `link`: that is a void
            // HTML element, and Trans would draw it empty with the words after it.
            anchor: (
              <InAppAnchor href={SUPPORT_PLACES.support} size="sm">
                {null}
              </InAppAnchor>
            ),
          }}
        />
      </Text>
    </Stack>
  );
}

function TeamAddresses() {
  const { t } = useTranslation('engagement');
  // The Support page's own words for the two addresses, read here so a change of language redraws.
  const { t: support } = useTranslation('support');
  const contacts = usePlatformContacts();

  if (contacts.status === 'loading') {
    return (
      <Text size="sm" c="dimmed">
        {t('inbox.team.reading')}
      </Text>
    );
  }
  if (contacts.status === 'failed' || contacts.data === null) {
    return (
      <Group gap="sm" wrap="wrap">
        <Text size="sm">{t('inbox.team.failed')}</Text>
        <Button size="xs" variant="default" loading={contacts.reloading} onClick={contacts.reload}>
          {support('page.contactRetry')}
        </Button>
      </Group>
    );
  }

  const { supportEmail, editorialEmail } = contacts.data;
  if (supportEmail === null && editorialEmail === null) {
    return <Text size="sm">{t('inbox.team.noneYet')}</Text>;
  }
  return (
    <Stack gap="md">
      {supportEmail === null ? null : (
        <Address
          label={support('page.contactSupportLabel')}
          gloss={support('page.contactSupportGloss')}
          address={supportEmail}
        />
      )}
      {editorialEmail === null ? null : (
        <Address
          label={support('page.contactEditorialLabel')}
          gloss={support('page.contactEditorialGloss')}
          address={editorialEmail}
        />
      )}
      {supportEmail === null || editorialEmail === null ? (
        // Say which half is missing rather than letting one address look like the whole answer.
        <Text size="xs" c="dimmed">
          {support('page.contactOneMissingSuffix')}
        </Text>
      ) : null}
    </Stack>
  );
}

function Address({
  label,
  gloss,
  address,
}: {
  readonly label: string;
  readonly gloss: string;
  readonly address: string;
}) {
  return (
    <div>
      <Group gap="sm" wrap="wrap">
        <Text size="sm" fw={700}>
          {label}
        </Text>
        <Anchor size="sm" href={`mailto:${address}`}>
          {address}
        </Anchor>
      </Group>
      <Text size="xs" c="dimmed">
        {gloss}
      </Text>
    </div>
  );
}
